import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AppLayout from "../../components/AppLayout";
import API_BASE_URL from "../../config/api";
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";

export default function ExpensePaymentHistory() {
  const navigate = useNavigate();
  const { expenseId } = useParams();

  const [expense, setExpense] = useState(null);
  const [payments, setPayments] = useState([]);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [totalPaid, setTotalPaid] = useState(0);
  const [expenseAmount, setExpenseAmount] = useState(0);
  
  // Payment dialog states
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  
  // Menu states for actions
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    paymentMethod: "Cash",
    paymentStatus: "Completed",
    paymentDate: new Date().toISOString().split("T")[0],
    notes: "",
  });

  useEffect(() => {
    fetchExpenseData();
    fetchPaymentHistory();
  }, [expenseId]);

  const fetchExpenseData = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/expense-payments/${expenseId}/balance`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setExpense(data.data.expense);
        setCurrentBalance(data.data.currentBalance);
        setTotalPaid(data.data.totalPaid);
        setExpenseAmount(data.data.expenseAmount);
      } else {
        toast.error("Failed to load expense details");
        navigate("/expenses");
      }
    } catch (error) {
      console.error("Error fetching expense:", error);
      toast.error("Failed to load expense details");
    }
  };

  const fetchPaymentHistory = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/expense-payments/${expenseId}/payments`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setPayments(data.data.payments || []);
      } else {
        toast.error("Failed to load payment history");
      }
    } catch (error) {
      console.error("Error fetching payment history:", error);
      toast.error("Failed to load payment history");
    }
  };

  const handleAddPayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error("Please enter a valid payment amount");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      
      if (editingPayment) {
        // Update existing payment
        const response = await fetch(`${API_BASE_URL}/api/expense-payments/${expenseId}/payments/${editingPayment.PaymentId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            amount: parseFloat(paymentForm.amount),
            paymentMethod: paymentForm.paymentMethod,
            paymentStatus: paymentForm.paymentStatus,
            paymentDate: paymentForm.paymentDate,
            notes: paymentForm.notes,
          }),
        });

        if (response.ok) {
          toast.success("Payment updated successfully");
        } else {
          toast.error("Failed to update payment");
          return;
        }
      } else {
        // Create new payment
        const response = await fetch(`${API_BASE_URL}/api/expense-payments/${expenseId}/payments`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            amount: parseFloat(paymentForm.amount),
            paymentMethod: paymentForm.paymentMethod,
            paymentStatus: paymentForm.paymentStatus,
            paymentDate: paymentForm.paymentDate,
            notes: paymentForm.notes,
          }),
        });

        if (response.ok) {
          toast.success("Payment recorded successfully");
        } else {
          toast.error("Failed to record payment");
          return;
        }
      }

      setOpenPaymentDialog(false);
      setEditingPayment(null);
      setPaymentForm({
        amount: "",
        paymentMethod: "Cash",
        paymentStatus: "Completed",
        paymentDate: new Date().toISOString().split("T")[0],
        notes: "",
      });
      fetchPaymentHistory();
      fetchExpenseData();
    } catch (error) {
      console.error("Error saving payment:", error);
      toast.error("Failed to save payment");
    }
  };

  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    setPaymentForm({
      amount: Math.abs(payment.Amount),
      paymentMethod: payment.PaymentMethod || "Cash",
      paymentStatus: payment.PaymentStatus || "Completed",
      paymentDate: payment.PaymentDate.split("T")[0],
      notes: payment.Notes || "",
    });
    setOpenPaymentDialog(true);
    setAnchorEl(null);
  };

  const handleDeleteClick = (payment) => {
    setPaymentToDelete(payment);
    setDeleteDialogOpen(true);
    setAnchorEl(null);
  };

  const handleMenuClick = (event, payment) => {
    setAnchorEl(event.currentTarget);
    setSelectedPayment(payment);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPayment(null);
  };

  const handleDeleteConfirm = async () => {
    if (!paymentToDelete) return;

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/expense-payments/${expenseId}/payments/${paymentToDelete.PaymentId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success("Payment deleted successfully");
        fetchPaymentHistory();
        fetchExpenseData();
      } else {
        toast.error("Failed to delete payment");
      }
    } catch (error) {
      console.error("Error deleting payment:", error);
      toast.error("Failed to delete payment");
    } finally {
      setDeleteDialogOpen(false);
      setPaymentToDelete(null);
    }
  };

  if (!expense) {
    return (
      <AppLayout>
        <Box sx={{ 
          p: 4, 
          textAlign: "center",
          bgcolor: "#0a0a0a",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}>
          <Typography sx={{ color: "#C59A58", fontSize: "1.2rem" }}>
            Loading...
          </Typography>
        </Box>
      </AppLayout>
    );
  }

  const isOwed = currentBalance > 0;

  return (
    <AppLayout>
      <Box sx={{ 
        maxWidth: "1400px", 
        mx: "auto", 
        p: { xs: 1, sm: 2, md: 3 },
        bgcolor: "#0a0a0a",
        minHeight: "100vh"
      }}>
        {/* HEADER */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2, flexWrap: "wrap", gap: 1.5 }}>
          <Typography variant="h3" sx={{ 
            fontWeight: 800, 
            letterSpacing: { xs: 1, sm: 2 },
            color: "#C59A58",
            fontSize: { xs: "1.25rem", sm: "1.75rem", md: "2.5rem" },
            wordBreak: "break-word",
            flex: 1,
            minWidth: 0
          }}>
            EXPENSE PAYMENTS
          </Typography>
          <Chip 
            label={`Expense: ${expense.FormattedDate}`} 
            sx={{ 
              bgcolor: "#C59A58", 
              color: "#000", 
              fontWeight: 600,
              fontSize: { xs: "0.7rem", sm: "0.85rem", md: "1rem" },
              px: { xs: 1, sm: 1.5, md: 2 },
              py: { xs: 1.5, sm: 2, md: 2.5 },
              height: { xs: 28, sm: 36, md: 40 },
              minWidth: "fit-content"
            }} 
          />
        </Box>

        {/* BACK BUTTON */}
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/expenses")}
          sx={{ 
            mb: 2,
            borderColor: "#C59A58",
            color: "#C59A58",
            "&:hover": { borderColor: "#b68649", bgcolor: "rgba(197, 154, 88, 0.04)" },
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
            px: { xs: 1.5, sm: 2, md: 3 },
            py: { xs: 0.75, sm: 1 }
          }}
        >
          Back to Expenses
        </Button>

        {/* EXPENSE INFO CARD */}
        <Paper elevation={6} sx={{ 
          p: { xs: 1.5, sm: 2, md: 3 }, 
          mb: 3, 
          borderRadius: 2, 
          background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
          width: "100%"
        }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 600, 
              color: "#C59A58",
              fontSize: { xs: "0.95rem", sm: "1.1rem", md: "1.25rem" }
            }}>
              Expense Information
            </Typography>
          </Box>
          
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ mb: 0.4, color: "#C59A58", fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.875rem" } }}>
                Expense Period
              </Typography>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                color: "#fff",
                fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.25rem" }
              }}>
                {expense.FormattedDate}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ mb: 0.4, color: "#C59A58", fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.875rem" } }}>
                Total Expense Amount
              </Typography>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                color: "#fff",
                fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.25rem" }
              }}>
                ₹{expenseAmount.toLocaleString('en-IN')}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ mb: 0.4, color: "#C59A58", fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.875rem" } }}>
                Total Paid
              </Typography>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                color: "#4caf50",
                fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.25rem" }
              }}>
                ₹{totalPaid.toLocaleString('en-IN')}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ mb: 0.4, color: "#C59A58", fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.875rem" } }}>
                Current Balance Due
              </Typography>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                color: isOwed ? "#d32f2f" : "#4caf50",
                fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.25rem" }
              }}>
                ₹{Math.abs(currentBalance).toLocaleString('en-IN')} {isOwed ? "(Due)" : "(Paid)"}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* PAYMENT HISTORY HEADER */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, mb: 2, flexDirection: { xs: "column", sm: "row" }, gap: 1.5 }}>
          <Typography variant="h5" sx={{ 
            fontWeight: 700,
            color: "#C59A58",
            fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" }
          }}>
            Payment History
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingPayment(null);
              setPaymentForm({
                amount: "",
                paymentMethod: "Cash",
                paymentStatus: "Completed",
                paymentDate: new Date().toISOString().split("T")[0],
                notes: "",
              });
              setOpenPaymentDialog(true);
            }}
            sx={{ 
              bgcolor: "#C59A58", 
              "&:hover": { bgcolor: "#b68649" },
              fontWeight: 600,
              color: "#000",
              fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.875rem" },
              px: { xs: 1.5, sm: 2, md: 3 },
              py: { xs: 0.6, sm: 0.8 },
              width: { xs: "100%", sm: "auto" }
            }}
          >
            Record Payment
          </Button>
        </Box>

        {/* PAYMENT HISTORY TABLE */}
        <Paper elevation={6} sx={{ 
          p: { xs: 0.75, sm: 1, md: 2 }, 
          borderRadius: 2, 
          bgcolor: "#1a1a1a",
          border: "1px solid #333",
          boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          width: "100%",
          overflowX: "auto"
        }}>
          <Box sx={{ overflowX: "auto", width: "100%" }}>
            <TableContainer sx={{ 
              borderRadius: 2,
              minWidth: { xs: "100%", sm: 500, md: 600 }
            }}>
              <Table
                sx={{
                  borderCollapse: "separate",
                  borderSpacing: "0 8px",
                }}
                aria-label="payment history table"
              >
                <TableHead>
                  <TableRow sx={{ bgcolor: "#C59A58" }}>
                    <TableCell sx={{ 
                      color: "#000", 
                      fontWeight: 700, 
                      border: "none",
                      fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
                      p: { xs: 0.75, sm: 1, md: 1.5 },
                      whiteSpace: "nowrap"
                    }}>
                      Date
                    </TableCell>
                    <TableCell sx={{ 
                      color: "#000", 
                      fontWeight: 700, 
                      border: "none",
                      fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
                      p: { xs: 0.75, sm: 1, md: 1.5 },
                      whiteSpace: "nowrap"
                    }}>
                      Amount
                    </TableCell>
                    <TableCell sx={{ 
                      color: "#000", 
                      fontWeight: 700, 
                      border: "none",
                      fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
                      p: { xs: 0.75, sm: 1, md: 1.5 },
                      whiteSpace: "nowrap",
                      display: { xs: "none", sm: "table-cell" }
                    }}>
                      Method
                    </TableCell>
                    <TableCell sx={{ 
                      color: "#000", 
                      fontWeight: 700, 
                      border: "none",
                      fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
                      p: { xs: 0.75, sm: 1, md: 1.5 },
                      whiteSpace: "nowrap"
                    }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ 
                      color: "#000", 
                      fontWeight: 700, 
                      border: "none",
                      fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
                      p: { xs: 0.75, sm: 1, md: 1.5 },
                      whiteSpace: "nowrap",
                      display: { xs: "none", md: "table-cell" }
                    }}>
                      Notes
                    </TableCell>
                    <TableCell align="center" sx={{ 
                      color: "#000", 
                      fontWeight: 700, 
                      border: "none",
                      fontSize: { xs: "0.65rem", sm: "0.75rem", md: "0.875rem" },
                      p: { xs: 0.75, sm: 1, md: 1.5 },
                      whiteSpace: "nowrap",
                      width: "60px"
                    }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.length > 0 ? (
                    payments.map((payment, index) => (
                      <TableRow
                        key={payment.PaymentId}
                        sx={{
                          bgcolor: index % 2 === 0 ? "#2a2a2a" : "#333",
                          borderRadius: 1,
                          "&:hover": { boxShadow: 1 },
                          "& td": { 
                            border: "none", 
                            verticalAlign: "middle",
                            fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.875rem" },
                            p: { xs: 0.6, sm: 0.8, md: 1.5 }
                          },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600, color: "#C59A58", whiteSpace: "nowrap" }}>
                          {new Date(payment.PaymentDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#4caf50", whiteSpace: "nowrap" }}>
                          ₹{Math.abs(payment.Amount).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell sx={{ color: "#fff", display: { xs: "none", sm: "table-cell" }, whiteSpace: "nowrap" }}>
                          {payment.PaymentMethod}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Chip
                            label={payment.PaymentStatus}
                            size="small"
                            sx={{
                              bgcolor: payment.PaymentStatus === "Completed" ? "#2e7d32" : 
                                       payment.PaymentStatus === "Pending" ? "#ed6c02" : "#1976d2",
                              color: "white",
                              fontWeight: 600,
                              fontSize: { xs: "0.6rem", sm: "0.7rem" }
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ 
                          maxWidth: 150, 
                          overflow: "hidden", 
                          textOverflow: "ellipsis", 
                          color: "#fff",
                          display: { xs: "none", md: "table-cell" },
                          whiteSpace: "nowrap"
                        }}>
                          {payment.Notes || "-"}
                        </TableCell>
                        <TableCell align="center" sx={{ width: "60px" }}>
                          <IconButton
                            onClick={(e) => handleMenuClick(e, payment)}
                            sx={{ 
                              color: "#C59A58",
                              p: { xs: 0.5, sm: 0.8 }
                            }}
                            size="small"
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 2, border: "none" }}>
                        <Typography variant="body1" sx={{ 
                          color: "#888",
                          fontSize: { xs: "0.7rem", sm: "0.8rem", md: "1rem" }
                        }}>
                          No payment history yet. Record your first payment!
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Paper>
      </Box>

      <Dialog 
        open={openPaymentDialog} 
        onClose={() => {
          setOpenPaymentDialog(false);
          setEditingPayment(null);
        }} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#1a1a1a",
            border: "1px solid #333",
            borderRadius: 2,
            mx: { xs: 1, sm: 0 }
          }
        }}
      >
        <DialogTitle sx={{ color: "#C59A58", fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" }, p: { xs: 1.5, sm: 2 } }}>
          {editingPayment ? "Edit Payment" : "Record Payment"}
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid item xs={12}>
                <TextField
                  label="Payment Amount (₹)"
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  fullWidth
                  required
                  slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#444' },
                      '&:hover fieldset': { borderColor: '#C59A58' },
                      '&.Mui-focused fieldset': { borderColor: '#C59A58' },
                      bgcolor: '#2a2a2a',
                      color: '#fff'
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#C59A58' }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Payment Method"
                  value={paymentForm.paymentMethod}
                  onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })}
                  fullWidth
                  slotProps={{ 
                    select: { 
                      native: true,
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#444' },
                      '&:hover fieldset': { borderColor: '#C59A58' },
                      '&.Mui-focused fieldset': { borderColor: '#C59A58' },
                      bgcolor: '#2a2a2a',
                      color: '#fff'
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#C59A58' }
                  }}
                >
                  <option value="Cash">Cash</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cheque">Cheque</option>
                  <option value="UPI">UPI</option>
                  <option value="Card">Card</option>
                  <option value="Other">Other</option>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Payment Status"
                  value={paymentForm.paymentStatus}
                  onChange={(e) => setPaymentForm({ ...paymentForm, paymentStatus: e.target.value })}
                  fullWidth
                  slotProps={{ 
                    select: { 
                      native: true,
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#444' },
                      '&:hover fieldset': { borderColor: '#C59A58' },
                      '&.Mui-focused fieldset': { borderColor: '#C59A58' },
                      bgcolor: '#2a2a2a',
                      color: '#fff'
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#C59A58' }
                  }}
                >
                  <option value="Completed">Completed</option>
                  <option value="Pending">Pending</option>
                  <option value="Failed">Failed</option>
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Payment Date"
                  type="date"
                  value={paymentForm.paymentDate}
                  onChange={(e) => setPaymentForm({ ...paymentForm, paymentDate: e.target.value })}
                  fullWidth
                  required
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#444' },
                      '&:hover fieldset': { borderColor: '#C59A58' },
                      '&.Mui-focused fieldset': { borderColor: '#C59A58' },
                      bgcolor: '#2a2a2a',
                      color: '#fff'
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#C59A58' }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Notes"
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Add any additional notes about this payment..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#444' },
                      '&:hover fieldset': { borderColor: '#C59A58' },
                      '&.Mui-focused fieldset': { borderColor: '#C59A58' },
                      bgcolor: '#2a2a2a',
                      color: '#fff'
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#C59A58' }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Button 
            onClick={() => {
              setOpenPaymentDialog(false);
              setEditingPayment(null);
            }}
            sx={{ color: '#888' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddPayment} 
            variant="contained" 
            sx={{ 
              bgcolor: "#C59A58", 
              "&:hover": { bgcolor: "#b68649" },
              color: "#000",
              fontWeight: 600
            }}
          >
            {editingPayment ? "Update Payment" : "Record Payment"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: "#1a1a1a",
            border: "1px solid #333",
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ color: "#C59A58" }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#fff" }}>
            Are you sure you want to delete this payment? This will recalculate the expense balance.
          </Typography>
          {paymentToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: "#2a2a2a", borderRadius: 1, border: "1px solid #444" }}>
              <Typography variant="body2" sx={{ color: "#fff" }}>
                <strong style={{ color: "#C59A58" }}>Amount:</strong> ₹{Math.abs(paymentToDelete.Amount).toLocaleString('en-IN')}
              </Typography>
              <Typography variant="body2" sx={{ color: "#fff" }}>
                <strong style={{ color: "#C59A58" }}>Date:</strong> {new Date(paymentToDelete.PaymentDate).toLocaleDateString()}
              </Typography>
              {paymentToDelete.Notes && (
                <Typography variant="body2" sx={{ color: "#fff" }}>
                  <strong style={{ color: "#C59A58" }}>Notes:</strong> {paymentToDelete.Notes}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: "#888" }}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            bgcolor: "#2a2a2a",
            border: "1px solid #444",
            borderRadius: 1,
            minWidth: 120
          }
        }}
      >
        <MenuItem 
          onClick={() => handleEditPayment(selectedPayment)}
          sx={{ 
            color: "#fff",
            fontSize: "0.875rem",
            "&:hover": { bgcolor: "#444" }
          }}
        >
          <EditIcon sx={{ mr: 1, fontSize: "1rem", color: "#C59A58" }} />
          Edit
        </MenuItem>
        <MenuItem 
          onClick={() => handleDeleteClick(selectedPayment)}
          sx={{ 
            color: "#fff",
            fontSize: "0.875rem",
            "&:hover": { bgcolor: "#444" }
          }}
        >
          <DeleteIcon sx={{ mr: 1, fontSize: "1rem", color: "#f44336" }} />
          Delete
        </MenuItem>
      </Menu>
    </AppLayout>
  );
}